import { useState, useEffect, useRef, useMemo } from 'react';
import { api } from '../lib/api.js';
import { toast, errText } from '../lib/toast.js';
import { PayrollTrendsView } from './PayrollTrendsView.jsx';
import { PayrollBonusesView } from './PayrollBonusesView.jsx';
import { PayrollTotalsBlock } from './PayrollTotalsBlock.jsx';
import { PtoPayoutCards } from './PtoPayoutCards.jsx';
import { ShiftDiscrepancyReport } from './ShiftDiscrepancyReport.jsx';

// ─── Reports → Payroll ─────────────────────────────────────────────────────
export function PayrollReportView({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  // Pay-period list: Mon→Sun pairs, newest first. CJ feedback 2026-05-09:
  // dropdown over free-form date so it can never be a non-Monday + clearly
  // shows the payroll cadence. CJ 2026-07-09: the in-progress week is
  // selectable too, so daily cash counts can be watched during the week —
  // but the default stays on the last completed week (the payroll flow),
  // and payroll actions are locked while a week is in progress.
  const periods = useMemo(() => {
    const out = [];
    const d = new Date(); d.setUTCHours(12, 0, 0, 0);
    const dow = d.getUTCDay(); const off = dow === 0 ? 6 : dow - 1;
    d.setUTCDate(d.getUTCDate() - off); // this week's Monday (in progress)
    for (let i = 0; i < 13; i++) {
      const start = new Date(d); start.setUTCDate(start.getUTCDate() - 7 * i);
      const end = new Date(start); end.setUTCDate(end.getUTCDate() + 6);
      out.push({
        weekStart: start.toISOString().slice(0, 10),
        weekEnd: end.toISOString().slice(0, 10),
        inProgress: i === 0,
      });
    }
    return out;
  }, []);
  const [weekStart, setWeekStart] = useState(() => periods[1]?.weekStart || '');
  const inProgress = weekStart === periods[0]?.weekStart;
  const [counts, setCounts] = useState([]);
  const [edits, setEdits] = useState({});  // { [location_id]: { counted_cash?, expected_cash?, cc_tips? } }
  const [preflight, setPreflight] = useState(null);
  const [run, setRun] = useState(null);
  // CJ feedback 2026-05-09: don't auto-expand the existing run.
  // showRun gates the Results section; toggled by a "Show report" /
  // "Hide report" button after the user confirms which period they
  // care about. Re-running payroll auto-shows.
  const [showRun, setShowRun] = useState(false);
  // PTO that has been approved (or cancelled, or edited) since this week's run
  // was generated. A run is a snapshot and nothing re-checks it, which is how
  // 18 approved hours were debited from Kiara Derobbio's balance and paid by
  // nobody in the week of 2026-07-06.
  const [ptoDrift, setPtoDrift] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  function weekEnd() {
    const d = new Date(weekStart + 'T12:00:00Z'); d.setUTCDate(d.getUTCDate() + 6);
    return d.toISOString().slice(0, 10);
  }
  function fmtPeriodLabel(p) {
    const fmt = (s) => new Date(s + 'T12:00:00Z')
      .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
    return `${fmt(p.weekStart)} – ${fmt(p.weekEnd)}`;
  }

  async function loadCounts() {
    try {
      const r = await api('GET', `/api/admin/payroll/cash-counts?week_start=${weekStart}`, null, headers);
      setCounts(r.counts || []);
      setEdits({});
    } catch (e) { setErr(e.message || String(e)); }
  }

  function fieldValue(c, field) {
    const e = edits[c.location_id];
    if (e && Object.prototype.hasOwnProperty.call(e, field)) return e[field];
    return c[field];
  }
  function setField(locId, field, raw) {
    const v = raw === '' ? null : Number(raw);
    setEdits((cur) => ({ ...cur, [locId]: { ...(cur[locId] || {}), [field]: Number.isFinite(v) ? v : null } }));
  }
  const dirtyCount = Object.values(edits).reduce((n, e) => n + Object.keys(e).length, 0);
  const isDirty = dirtyCount > 0;
  async function loadPreflight() {
    try {
      const r = await api('GET', `/api/admin/payroll/preflight?week_start=${weekStart}`, null, headers);
      setPreflight(r);
    } catch (e) { setErr(e.message || String(e)); }
  }
  async function loadExistingRun() {
    try {
      const r = await api('GET', `/api/admin/payroll/run?week_start=${weekStart}`, null, headers);
      setRun(r.run ? { totals: r.run.totals, lines: r.lines } : null);
      setPtoDrift(Array.isArray(r.pto_drift) ? r.pto_drift : []);
    } catch (e) { setRun(null); setPtoDrift([]); }
  }

  // Counts + preflight always reload on period change. The existing
  // run loads silently into state too, so the "Show report" button can
  // surface it instantly when clicked. setShowRun(false) on every
  // period change keeps the report collapsed by default.
  // Running payroll + reading the chain-wide wage detail is owner/admin
  // (run_payroll, systems review 2026-07-23). Managers keep this screen for
  // cash counts, trends, and bonuses — the run/preflight/downloads sections
  // simply never load (server enforces the same gate).
  const canRunPayroll = !!window.EmpireRoles?.hasCapability?.(currentUser, 'run_payroll');
  // Owner-only as of CJ 2026-08-03. These set the figures a café's counted
  // drawer is measured AGAINST, and one pull rewrites all four cafés at once.
  const canPullSquareFinancials =
    !!window.EmpireRoles?.hasCapability?.(currentUser, 'pull_square_financials');

  useEffect(() => {
    setRun(null);
    setShowRun(false);
    loadCounts();
    // An in-progress week has no payroll to preflight or show — and a
    // half-week preflight would just be a wall of spurious issues.
    if (inProgress || !canRunPayroll) { setPreflight(null); return; }
    loadPreflight();
    loadExistingRun();
  }, [weekStart]);

  async function saveAll() {
    if (!isDirty) return;
    setBusy(true);
    try {
      const entries = Object.entries(edits);
      for (const [locId, patch] of entries) {
        if (!patch || Object.keys(patch).length === 0) continue;
        await api('PUT', `/api/admin/payroll/cash-counts/${locId}`,
          { week_start: weekStart, ...patch }, headers);
      }
      setSavedAt(new Date());
      await loadCounts();
      await loadPreflight();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  }
  // Persistent results panel so the tip breakdown stays visible.
  // CJ feedback 2026-05-11: an alert() popup was easy to dismiss and
  // didn't show up in screenshots — we need to be able to read the
  // per-tender breakdown next to Square's CSV side-by-side.
  const [lastPull, setLastPull] = useState(null);
  // Seconds the current pull has been running. A Square pull is genuinely
  // slow — CJ 2026-08-11 measured 63-92s in prod — and a button that only says
  // "Pulling…" for a minute and a half is indistinguishable from a hung one.
  // That ambiguity is what caused the reload-mid-pull loop; a ticking counter
  // is the whole fix for it.
  // `busy` is shared by every button on this toolbar, so it cannot say whether
  // THIS pull is the thing running — a timecard sync would read as "Pulling…".
  const [pulling, setPulling] = useState(false);
  const [pullSecs, setPullSecs] = useState(0);
  async function pullSquare() {
    setBusy(true);
    setPulling(true);
    setPullSecs(0);
    const started = Date.now();
    const tick = setInterval(() => {
      setPullSecs(Math.round((Date.now() - started) / 1000));
      // Every cafe's row is written the instant its own pull finishes, so
      // re-read while the request is still in flight: numbers land one cafe at
      // a time instead of all at the end.
      loadCounts();
    }, 5000);
    try {
      const r = await api('POST', '/api/admin/payroll/cash-counts/pull-square',
        { week_start: weekStart }, headers);
      setLastPull(r);
    } catch (e) { toast.error(errText(e)); }
    finally {
      clearInterval(tick);
      setPulling(false);
      setBusy(false);
      // Refresh even when the request failed. The server writes each cafe as
      // it goes, so a timeout or a dropped connection still leaves real rows
      // behind — refreshing only on success is what made those look lost.
      await loadCounts();
      await loadPreflight();
    }
  }
  // Import the "Tip" column from Square's Sales-report CSV. CJ feedback
  // 2026-05-11: until we identify the root cause of the Orders-API
  // tip discrepancy, managers paste Square's CSV export and we write
  // the per-location Tip values straight to cc_tips. Cash still pulls
  // from the live Square API.
  const csvInputRef = useRef(null);
  async function importTipsCsv(file) {
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const r = await api('POST', '/api/admin/payroll/cash-counts/import-csv',
        { week_start: weekStart, csv: text }, headers);
      const summary = (r?.results || []).map((row) => {
        if (row.skipped) return `${row.csv_location || row.location}: skipped (${row.skipped})`;
        return `${row.location}: cc_tips = $${Number(row.cc_tips || 0).toFixed(2)}`;
      }).join('\n');
      toast.info(`CSV import for ${weekStart} – ${weekEnd()}:\n\n${summary || 'No rows imported.'}`);
      await loadCounts();
      await loadPreflight();
    } catch (e) { toast.error(`Import failed: ${e.message || e}`); }
    finally { setBusy(false); if (csvInputRef.current) csvInputRef.current.value = ''; }
  }
  // Import Square's shifts-export CSV as this week's canonical timecards.
  // CJ 2026-07-15: Monarch generates the pay hours, so they must equal the
  // Square *report*. The Labor-API sync can't — Square classifies some breaks
  // as paid in the API but unpaid in the export (Michelle 0.12h). Pasting the
  // export makes Monarch reproduce the report to the minute.
  const shiftsInputRef = useRef(null);
  async function importShiftsCsv(file) {
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const r = await api('POST', '/api/admin/timeclock/import-shifts-csv',
        { week_start: weekStart, csv: text }, headers);
      const summary = (r?.results || []).map((row) =>
        row.skipped ? `${row.location}: skipped (${row.skipped})`
                    : `${row.location}: ${row.imported} shifts`).join('\n');
      const extra = [];
      if (r.replaced) extra.push(`${r.replaced} existing punches replaced`);
      if (r.orphaned) extra.push(`${r.orphaned} unmatched name(s): ${(r.orphan_names || []).join(', ')}`);
      if (r.out_of_week) extra.push(`${r.out_of_week} rows outside this week (ignored)`);
      toast.info(`Imported Square shifts for ${r.week_start} – ${r.week_end}:\n\n${summary}${extra.length ? '\n\n' + extra.join('\n') : ''}`);
      await loadPreflight();
    } catch (e) { toast.error(`Import failed: ${e.message || e}`); }
    finally { setBusy(false); if (shiftsInputRef.current) shiftsInputRef.current.value = ''; }
  }
  // Sync Square's actual clock-in/out records into Monarch's
  // timeclock_punches for this week. CJ feedback 2026-05-11: needed
  // while Monarch's own timeclock is in test so managers can review +
  // certify on real data.
  async function syncSquareTimecards() {
    setBusy(true);
    try {
      const r = await api('POST', '/api/admin/timeclock/sync-from-square',
        { week_start: weekStart }, headers);
      const summary = (r?.results || []).map((row) => {
        if (row.skipped) return `${row.location}: skipped (${row.skipped})`;
        if (row.error)   return `${row.location}: error — ${row.error}`;
        const parts = [];
        if (row.imported) parts.push(`${row.imported} from Square`);
        if (row.replaced) parts.push(`${row.replaced} replaced`);
        if (row.orphaned) parts.push(`${row.orphaned} skipped (no match)`);
        return `${row.location}: ${parts.join(', ') || 'no changes'}`;
      }).join('\n');
      toast.info(`Square timecard sync for ${r.start_date} – ${r.end_date}:\n\n${summary}`);
      await loadPreflight();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  }
  async function runPayroll() {
    setBusy(true); setErr('');
    try {
      const r = await api('POST', '/api/admin/payroll/run', { week_start: weekStart }, headers);
      // Always set totals to a truthy object so the results section renders.
      // Either entity may legitimately have zero employees; PayrollTotalsBlock
      // shows "No data." when its `t` prop is null, which is the right UX.
      setRun({
        totals: { etc: r.etc?.totals || null, nbt: r.nbt?.totals || null, locations: r.locations || [],
                  cash_warnings: r.cash_warnings || [] },
        lines: [...((r.etc?.lines) || []), ...((r.nbt?.lines) || [])],
      });
      setShowRun(true); // re-running auto-expands; viewing the result is the point
      loadPreflight();
    } catch (e) {
      // Previously, the catch did `JSON.parse(e.message); if (parsed.issues)…`
      // and silently dropped any error that wasn't a preflight 409. That
      // turned 500s and other server failures into "click does nothing,"
      // which is exactly what was happening to the run-payroll button.
      let parsed = null;
      try { parsed = JSON.parse(e.message); } catch (_) {}
      if (parsed && parsed.issues) {
        setPreflight({ blocked: true, issues: parsed.issues });
      } else {
        setErr((parsed && parsed.error) || e.message || String(e));
      }
    }
    finally { setBusy(false); }
  }

  // Fetch the payroll PDF (or bundle .zip) as a blob with proper auth
  // headers, then open/download it. The legacy `?u=<id>` query-string auth
  // was removed by the Phase 4 (C5) retrofit, so a plain <a href> /
  // window.open hits the umbrella with no auth and 401s — which the user
  // experiences as "no PDF appears." Blob download works because we can
  // attach Authorization / x-user-id headers to a fetch().
  async function downloadPdf(kind) {
    try {
      const headers = {};
      const token = (typeof localStorage !== 'undefined') ? localStorage.getItem('monarchIdToken') : null;
      if (token) headers['Authorization'] = `Bearer ${token}`;
      if (currentUser?.id) headers['x-user-id'] = String(currentUser.id);
      const res = await fetch(`/api/admin/payroll/pdf/${kind}?week_start=${weekStart}`, { headers });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const isBundle = kind === 'bundle';
      if (isBundle) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `payroll-${weekStart}.zip`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Could not download payroll report: ' + errText(e));
    }
  }

  // Sub-tab between the per-period payroll view, the weekly-trends
  // view, and the new bonuses queue. CJ feedback 2026-05-09 + 2026-05-11.
  const [subView, setSubView] = useState('payroll'); // 'payroll' | 'trends' | 'bonuses'

  return (
    <div className="space-y-4">
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e0e0e0' }}>
        {[['payroll', 'Payroll'], ['trends', 'Weekly trends'], ['bonuses', 'Bonuses']].map(([id, label]) => (
          <button key={id} onClick={() => setSubView(id)}
            style={{ padding: '10px 16px', fontSize: 13, fontWeight: 600,
                      background: 'transparent', border: 'none',
                      borderBottom: `2px solid ${subView === id ? 'var(--forest)' : 'transparent'}`,
                      color: subView === id ? '#212121' : '#757575', cursor: 'pointer',
                      fontFamily: 'inherit', marginBottom: -1 }}>
            {label}
          </button>
        ))}
      </div>

      {subView === 'trends'  && <PayrollTrendsView  currentUser={currentUser} />}
      {subView === 'bonuses' && <PayrollBonusesView currentUser={currentUser} />}

      {subView === 'payroll' && <>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="text-base font-semibold">Payroll</div>
            <div className="text-xs text-gray-500">Pay period (Mon–Sun): {weekStart} – {weekEnd()}</div>
          </div>
          <select value={weekStart} onChange={(e) => setWeekStart(e.target.value)}
            style={{ padding: 8, border: '1px solid #d0d4d9', borderRadius: 6, fontFamily: 'inherit', minWidth: 200 }}>
            {periods.map((p, i) => (
              <option key={p.weekStart} value={p.weekStart}>
                {fmtPeriodLabel(p)}{p.inProgress ? ' · this week (in progress)' : i === 1 ? ' · last completed' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {inProgress && (
        <div className="card" style={{ borderLeft: '4px solid #b48a47', fontSize: 13 }}>
          This week is still in progress — the daily counts below update as stores submit them.
          Square pulls and payroll unlock when the week ends.
        </div>
      )}

      {/* Square pull diagnostic — shows the per-location tip breakdown
          so the manager can reconcile against Square's CSV. CJ 2026-05-11. */}
      {lastPull && Array.isArray(lastPull.results) && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                          flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
            <div>
              <div className="text-base font-semibold">Square pull breakdown</div>
              <div className="text-xs text-gray-500">
                Per-location tip aggregation. Compare each row to Square's CSV "Tip" column.
              </div>
            </div>
            <button onClick={() => setLastPull(null)}
              style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #d0d4d9',
                        borderRadius: 4, background: '#fff', cursor: 'pointer' }}>Dismiss</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#666', borderBottom: '1px solid #e0e0e0' }}>
                  <th style={{ padding: 6 }}>Location</th>
                  <th style={{ padding: 6, textAlign: 'right' }}>cc_tips (saved)</th>
                  <th style={{ padding: 6, textAlign: 'right' }}>via orders</th>
                  <th style={{ padding: 6, textAlign: 'right' }}>via tenders</th>
                  <th style={{ padding: 6, textAlign: 'right' }}>card-only</th>
                  <th style={{ padding: 6, textAlign: 'right' }}>cash-only</th>
                  <th style={{ padding: 6, textAlign: 'right' }}>mixed</th>
                  <th style={{ padding: 6, textAlign: 'right' }}>other</th>
                  <th style={{ padding: 6, textAlign: 'right' }}>declared cash</th>
                </tr>
              </thead>
              <tbody>
                {lastPull.results.map((row, i) => {
                  if (row.error) return (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', color: '#b00020' }}>
                      <td style={{ padding: 6 }}>{row.location}</td>
                      <td style={{ padding: 6 }} colSpan={7}>error — {row.error}</td>
                    </tr>
                  );
                  if (row.skipped) return (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', color: '#888' }}>
                      <td style={{ padding: 6 }}>{row.location}</td>
                      <td style={{ padding: 6 }} colSpan={7}>{row.skipped}</td>
                    </tr>
                  );
                  const d = row.debug || {};
                  const cell = (n) => `$${Number(n || 0).toFixed(2)}`;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: 6, fontWeight: 600 }}>{row.location}</td>
                      <td style={{ padding: 6, textAlign: 'right', fontWeight: 600 }}>{cell(row.cc_tips)}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>{cell(d.total_tips_via_orders)}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>{cell(d.total_tips_via_tenders)}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>{cell(d.tips_on_card_only)}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>{cell(d.tips_on_cash_only)}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>{cell(d.tips_on_mixed)}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>{cell(d.tips_on_other_tenders)}</td>
                      <td style={{ padding: 6, textAlign: 'right' }}>{cell(d.declared_cash_tips)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Daily cash counts — per-location, per-day of the selected week.
          Managers certify or backfill from here; certified totals roll
          into the weekly counted_cash below in a follow-up. */}
      {/* Cash counts */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="text-base font-semibold">Cash counts</div>
            <div className="text-xs text-gray-500">
              {canPullSquareFinancials
                ? 'Pull expected cash + CC tips from Square here.'
                : 'Expected cash + CC tips are pulled from Square by the owner.'}
              {' '}Count the drawer &amp; certify in{' '}
              <button onClick={() => window.dispatchEvent(new CustomEvent('monarch:navigate', { detail: 'reports-cash-counts' }))}
                style={{ background: 'none', border: 0, padding: 0, color: '#a8323a', textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}>
                Reports → Cash counts
              </button>.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* The Square-derived cash figures are owner-only (CJ 2026-08-03):
                they set what a café’s counted drawer is measured against, and
                one pull rewrites all four cafés. The two timecard imports below
                are deliberately NOT gated with them — different data, different
                risk. Sync Square timecards is manager-level on purpose (CJ
                2026-07-30: whoever certifies must be able to pull the latest),
                and Import shifts from CSV enforces owner/admin inside its own
                handler.

                A stale note here used to say sync-from-square returns 410 and
                that its button was hidden. Neither is true — the route was
                revived 2026-07-06 and widened 2026-07-30. Removed rather than
                left sitting above a permissions gate. */}
            {canPullSquareFinancials && (
              <>
                <button onClick={pullSquare} disabled={busy}
                  className="btn-secondary"
                  title="Fill expected cash + CC tips for every store from Square. Takes around half a minute — each store's numbers appear as they arrive.">
                  {pulling ? `Pulling… ${pullSecs}s (stores fill in as they arrive)` : 'Pull from Square'}
                </button>
                <input ref={csvInputRef} type="file" accept=".csv,text/csv"
                  style={{ display: 'none' }}
                  onChange={(e) => importTipsCsv(e.target.files?.[0])} />
                <button onClick={() => csvInputRef.current?.click()} disabled={busy || inProgress} className="btn-secondary"
                  title={inProgress ? 'Available once the week ends' : "Import the per-location Tip column from Square's Sales-report CSV"}>
                  Import tips from CSV
                </button>
              </>
            )}
            <button onClick={syncSquareTimecards} disabled={busy} className="btn-secondary"
              title="Bring Square clock-in/out records into Monarch for this week, so you can review and certify timecards before running payroll">
              Sync Square timecards
            </button>
            <input ref={shiftsInputRef} type="file" accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={(e) => importShiftsCsv(e.target.files?.[0])} />
            <button onClick={() => shiftsInputRef.current?.click()} disabled={busy} className="btn-secondary"
              title="Import Square's shifts-export CSV as this week's timecards — makes Monarch's pay hours match the report exactly (recommended for payroll)">
              Import shifts from CSV
            </button>
          </div>
        </div>
        <div style={{ overflowX: 'auto', marginTop: 12 }}>
          <table style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                <th style={{ padding: 6 }}>Location</th>
                <th style={{ padding: 6 }}>Counted cash</th>
                <th style={{ padding: 6 }}>Expected cash (Square)</th>
                <th style={{ padding: 6 }}>CC tips (Square)</th>
                <th style={{ padding: 6 }}>Source</th>
              </tr>
            </thead>
            <tbody>
              {counts.map((c) => {
                return (
                <tr key={c.location_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: 6, fontWeight: 600 }}>{c.location_name}</td>
                  {/* CJ 2026-08-11: this cell showed "—" for Bellevue for a
                      week whose cash was fully counted — seven daily drawer
                      counts, all certified. A cafe that runs the daily
                      workflow never keys a weekly figure, and payroll already
                      rolls the daily counts up for it. Showing a dash there
                      said "no cash recorded" about $2,971.21 that was. The
                      server now resolves this the same way the payroll engine
                      does, and the source is labelled so the two paths are
                      told apart at a glance. */}
                  <td style={{ padding: 6, fontVariantNumeric: 'tabular-nums' }}>
                    {c.counted_cash != null
                      ? `$${Number(c.counted_cash).toFixed(2)}`
                      : c.counted_cash_source === 'daily_rollup'
                        ? (<>
                            ${Number(c.effective_counted_cash).toFixed(2)}
                            <div style={{ fontSize: 10, color: '#757575', fontVariantNumeric: 'normal' }}>
                              from {c.daily_certified_days} daily counts
                            </div>
                          </>)
                        /* A part-counted week used to render as "—", identical
                           to a cafe that had done nothing — so "one drawer
                           count short" and "no cash recorded anywhere" looked
                           the same on the screen you check before running
                           payroll. It reads as progress mid-week (when being
                           short IS the normal state, every cafe, every day)
                           and as a gap once the week is closed. */
                        : c.counted_cash_source === 'daily_incomplete'
                          ? (<>
                              <span style={{ color: inProgress ? '#757575' : '#8a6b1a', fontWeight: 600 }}>
                                {c.daily_certified_days} of 7 days
                              </span>
                              <div style={{ fontSize: 10, color: '#757575', fontVariantNumeric: 'normal' }}>
                                {inProgress
                                  ? 'week in progress'
                                  : `${7 - c.daily_certified_days} still to certify`}
                              </div>
                            </>)
                          : '—'}
                  </td>
                  <td style={{ padding: 6, fontVariantNumeric: 'tabular-nums' }}>
                    {c.expected_cash != null ? `$${Number(c.expected_cash).toFixed(2)}` : '—'}
                  </td>
                  <td style={{ padding: 6, fontVariantNumeric: 'tabular-nums' }}>
                    {c.cc_tips != null ? `$${Number(c.cc_tips).toFixed(2)}` : '—'}
                  </td>
                  <td style={{ padding: 6, fontSize: 11, color: '#757575' }}>
                    {c.source === 'square_assisted' ? `Square${c.square_pulled_at ? ` · ${String(c.square_pulled_at).slice(0,10)}` : ''}` : 'Manual'}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Final checks — PTO payout (D246). Above the pre-flight on purpose:
          it is the one blocker a manager resolves here rather than on another
          screen, so it should be the first thing under the numbers. */}
      {canRunPayroll && !inProgress && (
        <PtoPayoutCards weekStart={weekStart} currentUser={currentUser} onChanged={loadPreflight} />
      )}

      {/* Shift discrepancies (CJ 2026-09-22) — schedule vs time clock for the
          week. Under the PTO cards and above the pre-flight: it is read while
          processing, but it blocks nothing, so it must not sit above the one
          panel that does.
          Spec: docs/SHIFT-DISCREPANCY-REPORT-SPEC.md */}
      {canRunPayroll && (
        <ShiftDiscrepancyReport weekStart={weekStart} currentUser={currentUser} onDownloadPdf={downloadPdf} />
      )}

      {/* Pre-flight */}
      {preflight && (
        <div className="card" style={{ borderLeft: `4px solid ${preflight.blocked ? '#c62828' : '#2e7d32'}` }}>
          <div className="text-base font-semibold mb-1">
            Pre-flight: {preflight.blocked ? `${preflight.issues.length} issue(s) — payroll blocked` : 'all clear'}
          </div>
          {preflight.blocked && (
            <ul style={{ fontSize: 13, marginLeft: 18, listStyleType: 'disc' }}>
              {preflight.issues.map((i, idx) => {
                const link = (tab, label) => (
                  <button onClick={() => window.dispatchEvent(new CustomEvent('monarch:navigate', { detail: tab }))}
                    style={{ background: 'none', border: 0, padding: 0, marginLeft: 6, color: '#a8323a',
                             textDecoration: 'underline', cursor: 'pointer', fontSize: 12 }}>
                    {label}
                  </button>
                );
                return (
                <li key={idx}>
                  {i.kind === 'missing_cash_count' && <>Cash count missing for {i.location}{link('reports-week-close', 'open Week close')}</>}
                  {i.kind === 'uncertified_week' && <>Time card not certified at {i.location}{link('reports-week-close', 'open Week close')}</>}
                  {i.kind === 'zero_wage' && <>{i.name} has no hourly rate set{link('settings-payroll-roster', 'open Pay & position')}</>}
                  {i.kind === 'missing_pay_entity' && <>{i.name} has no pay entity (ETC/NBT){link('settings-payroll-roster', 'open Pay & position')}</>}
                  {i.kind === 'missing_position' && <>{i.name} has no position assigned{link('settings-payroll-roster', 'open Pay & position')}</>}
                  {i.kind === 'pto_payout_undecided' && <>{i.name} is leaving {i.last_day} with {i.hours}h PTO — decide the payout below</>}
                  {i.code === 'holiday_tip_split' && <>{i.message}</>}
                  {!i.kind && !i.code && <>{i.message || JSON.stringify(i)}</>}
                </li>
                );
              })}
            </ul>
          )}
          <div style={{ marginTop: 12 }}>
            <button onClick={runPayroll} disabled={busy || preflight.blocked} className="btn-primary">
              {busy ? 'Running…' : run ? 'Re-run payroll' : 'Run payroll'}
            </button>
          </div>
          {err && <div className="text-xs text-red-700 mt-2">{err}</div>}
        </div>
      )}

      {/* PTO approved after this week's run was generated. It never reaches
          the run — a run is a stored snapshot — and until CJ went looking on
          2026-08-12 nothing had ever compared the two. Kiara Derobbio's 18
          hours were debited from her balance and paid by nobody because her
          approval landed 77 minutes after the run. Shown above the report
          rather than inside it: the number you would otherwise trust is the
          one that is wrong, so this has to arrive before you read it. */}
      {run?.totals && ptoDrift.length > 0 && (
        <div className="card" style={{ borderLeft: '4px solid #b8860b', background: '#fbf7ec' }}>
          <div className="text-sm font-semibold" style={{ color: '#8a6b1a' }}>
            PTO changed after this run was generated
          </div>
          <div className="text-xs text-gray-600" style={{ marginTop: 4 }}>
            This report does not include the PTO below. Re-run the week to pull it in —
            or, if the week has already been paid, settle it separately and leave the run as it is.
          </div>
          <table style={{ width: '100%', fontSize: 12, marginTop: 8, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee2c4' }}>
                <th style={{ padding: 4 }}>Employee</th>
                <th style={{ padding: 4 }}>Approved PTO</th>
                <th style={{ padding: 4 }}>Paid by this run</th>
                <th style={{ padding: 4 }}>Difference</th>
              </tr>
            </thead>
            <tbody>
              {ptoDrift.map((d) => (
                <tr key={d.user_id} style={{ borderBottom: '1px solid #f3ead6' }}>
                  <td style={{ padding: 4, fontWeight: 600 }}>{d.name}</td>
                  <td style={{ padding: 4, fontVariantNumeric: 'tabular-nums' }}>{d.expected_hours.toFixed(2)} h</td>
                  <td style={{ padding: 4, fontVariantNumeric: 'tabular-nums' }}>{d.paid_hours.toFixed(2)} h</td>
                  <td style={{ padding: 4, fontVariantNumeric: 'tabular-nums', fontWeight: 700,
                               color: d.delta_hours > 0 ? '#8a6b1a' : '#226b4a' }}>
                    {d.delta_hours > 0 ? '+' : ''}{d.delta_hours.toFixed(2)} h
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Show / hide existing run. CJ feedback 2026-05-09: don't auto-
          expand the report on period select; one extra click keeps the
          page tidy and matches behavior across periods. */}
      {run?.totals && !showRun && (
        <div className="card" style={{ display: 'flex', justifyContent: 'space-between',
                                          alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="text-sm font-semibold">Payroll report ready</div>
            <div className="text-xs text-gray-500">
              {fmtPeriodLabel({ weekStart, weekEnd: weekEnd() })} — already run.
            </div>
          </div>
          <button onClick={() => setShowRun(true)} className="btn-primary">Show report</button>
        </div>
      )}

      {/* Results */}
      {run?.totals && showRun && (
        <>
          {/* Cash-count flags (CJ 2026-07-22): shortage / count divergence /
              round-dollar counts. Non-blocking, but shown first so they're seen. */}
          {(run.totals.cash_warnings || []).length > 0 && (
            <div className="card" style={{ borderLeft: '4px solid #c62828', background: '#fff8f6' }}>
              <div className="text-sm font-semibold mb-1" style={{ color: '#b91c1c' }}>
                Cash-count flags — review before finalizing
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#4e342e' }}>
                {(run.totals.cash_warnings || []).map((w, i) => (
                  <li key={i} style={{ padding: '2px 0' }}>{w.message}</li>
                ))}
              </ul>
            </div>
          )}
          <div className="card">
            <div className="text-base font-semibold mb-2">ETC totals</div>
            <PayrollTotalsBlock t={run.totals.etc} />
          </div>
          <div className="card">
            <div className="text-base font-semibold mb-2">NBT totals</div>
            <PayrollTotalsBlock t={run.totals.nbt} />
          </div>
          <div className="card">
            <div className="text-base font-semibold mb-2">Per-employee detail</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0' }}>
                    <th style={{ padding: 6 }}>Name</th>
                    <th style={{ padding: 6 }}>Entity</th>
                    <th style={{ padding: 6 }}>Reg</th>
                    <th style={{ padding: 6 }}>OT</th>
                    <th style={{ padding: 6 }}>Hol</th>
                    <th style={{ padding: 6 }}>PTO</th>
                    {/* CJ 2026-08-19: "monarch told me about her payout for PTO
                        balance and I accepted it, it does not show up in the
                        payroll report." It was in the run all along —
                        pto_payout_hours/_pay were computed, stored on the line
                        and written to the database — and then rendered nowhere
                        and left out of Total Pay. Kiara Derobbio's $687.04
                        final payout was invisible AND missing from her total. */}
                    <th style={{ padding: 6 }}>PTO payout</th>
                    <th style={{ padding: 6 }}>Tips</th>
                    <th style={{ padding: 6 }}>Bonus</th>
                    <th style={{ padding: 6 }}>Total Pay</th>
                  </tr>
                </thead>
                <tbody>
                  {(run.lines || []).map((l) => (
                    <tr key={l.user_id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: 6, fontWeight: 600 }}>{l.name}</td>
                      <td style={{ padding: 6 }}>{l.pay_entity}</td>
                      <td style={{ padding: 6 }}>{Number(l.regular_hours).toFixed(2)}</td>
                      <td style={{ padding: 6 }}>{Number(l.overtime_hours).toFixed(2)}</td>
                      <td style={{ padding: 6 }}>{Number(l.holiday_hours).toFixed(2)}</td>
                      <td style={{ padding: 6 }}>{Number(l.pto_hours).toFixed(2)}</td>
                      <td style={{ padding: 6 }}>
                        {Number(l.pto_payout_pay || 0) > 0 ? (
                          <>
                            ${Number(l.pto_payout_pay).toFixed(2)}
                            <div style={{ fontSize: 10, color: '#757575' }}>
                              {Number(l.pto_payout_hours || 0).toFixed(2)}h final
                            </div>
                          </>
                        ) : '—'}
                      </td>
                      <td style={{ padding: 6 }}>${Number(l.tips).toFixed(2)}</td>
                      <td style={{ padding: 6 }}>${Number(l.bonus_pay || 0).toFixed(2)}</td>
                      {/* pto_payout_pay belongs in the total. Leaving it out did
                          not merely hide the payout — it under-stated what the
                          person is owed on their final cheque. */}
                      <td style={{ padding: 6 }}>${(Number(l.regular_pay) + Number(l.overtime_pay) + Number(l.holiday_pay) + Number(l.pto_pay) + Number(l.pto_payout_pay || 0) + Number(l.tips) + Number(l.bonus_pay || 0)).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card">
            <div className="text-base font-semibold mb-2">Downloads</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => downloadPdf('etc')} className="btn-secondary">ETC payroll PDF</button>
              <button onClick={() => downloadPdf('nbt')} className="btn-secondary">NBT payroll PDF</button>
              <button onClick={() => downloadPdf('location')} className="btn-secondary">Location breakdown PDF</button>
              <button onClick={() => downloadPdf('bundle')} className="btn-primary">All three (zip)</button>
            </div>
          </div>
        </>
      )}
      </>}
    </div>
  );
}
